import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    const lines = [line, ...this.#addonLines()];

    try {
      let errorMessage = '';
      try {
        const result = await window.Shopify.actions.updateCart(
          { lines },
          {
            event: {
              context: 'product',
              detail: this.#eventDetail(),
            },
            signal,
          }
        );
        this.#throwIfAborted(signal);
        errorMessage = this.#resultErrorMessage(result);
      } catch (error) {
        if (signal.aborted) return;
        errorMessage = getCartErrorMessage(error) || this.dataset.errorMessage || 'error';
      }

      // Fallback: if the standard cart action fails, add the same lines through the
      // AJAX cart endpoint, then refresh the cart drawer.
      if (errorMessage) {
        const fallbackError = await this.#addViaAjax(lines, signal);
        this.#throwIfAborted(signal);
        if (fallbackError) {
          this.#showError(fallbackError);
          return;
        }
        try {
          await customElements.get('s-cart')?.refreshMountedCarts?.({ signal });
        } catch (refreshError) {
          if (signal.aborted) return;
        }
      }

      await window.Shopify.actions.openCart?.();
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  async #addViaAjax(lines, signal) {
    const addUrl = this.dataset.cartAddUrl;
    if (!addUrl) return this.dataset.errorMessage || '';

    const items = lines.map((cartLine) => {
      const id = Number(String(cartLine.merchandiseId).split('/').pop());
      const item = { id, quantity: cartLine.quantity };
      if (cartLine.attributes?.length) {
        item.properties = Object.fromEntries(cartLine.attributes.map((a) => [a.key, a.value]));
      }
      if (cartLine.sellingPlanId) item.selling_plan = Number(String(cartLine.sellingPlanId).split('/').pop());
      return item;
    });

    try {
      const response = await fetch(`${addUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
        signal,
      });
      if (response.ok) return '';
      const data = await response.json().catch(() => ({}));
      return data.description || data.message || this.dataset.errorMessage || '';
    } catch (error) {
      if (signal.aborted) return '';
      return this.dataset.errorMessage || '';
    }
  }

  // Optional add-on products ticked next to the bundle cards. Each row holds a
  // checkbox [data-addon] and an element [data-addon-variant] with the variant ID.
  #addonLines() {
    const lines = [];
    for (const row of this.querySelectorAll('[data-addon-row]')) {
      const checkbox = row.querySelector('input[data-addon]');
      if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) continue;
      const variantControl = row.querySelector('[data-addon-variant]');
      const variantId = String(variantControl?.value || '').trim();
      if (!variantId) continue;
      lines.push({ merchandiseId: variantId, quantity: 1 });
    }
    return lines;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
